// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import * as Log from "@opencode-ai/core/util/log"
import { ConfigMCP } from "@/config/mcp"
import { InfcodeMcpConfig } from "@/infcode/mcp/config"
import { InfcodeMcpEnabled } from "@/infcode/mcp/enabled"
import { normalizeServerEntry } from "@/infcode/mcp/normalize"
import { Instance } from "@/project/instance"
import { discoverProjectDirectories, type ProjectDiscoveryIssue } from "./discovery"
import { addMigrationItem, createMigrationReport, type MigrationReport } from "./types"
import { errorMessage, exists, isRecord, stableStringify } from "./util"

const LOG_PREFIX = "[infcode:migration:mcp]"
const log = Log.create({ service: LOG_PREFIX })

type Scope = "project"

export type McpMigrationItem = {
  status: "migrated" | "rewritten" | "skipped" | "failed"
  scope: Scope
  sourcePath: string
  targetPath?: string
  backupPath?: string
  serverName?: string
  reason?: string
  message?: string
}

export type McpMigrationReport = MigrationReport<McpMigrationItem>

type McpDocument = {
  name: string
  version: string
  schema: string
  mcpServers: Record<string, unknown>
}

type ServerConversion = {
  name: string
  status: "migrated" | "skipped" | "failed"
  value: unknown
  enabled?: boolean
  reason?: string
  message?: string
}

export namespace McpMigration {
  export type Item = McpMigrationItem
  export type Report = McpMigrationReport

  export async function run(input: { home: string; projectDir?: string; projectId?: string }): Promise<McpMigrationReport> {
    const report = createMigrationReport<McpMigrationItem>()
    const targets = await discoverMcpProjects(input)

    for (const issue of targets.issues) {
      if (issue.kind === "sessions-json-invalid") {
        addMigrationItem(report, {
          status: "failed",
          scope: "project",
          sourcePath: issue.sourcePath,
          reason: "sessions-json-invalid",
          message: issue.message,
        })
        continue
      }
      addMigrationItem(report, {
        status: "skipped",
        scope: "project",
        sourcePath: issue.sourcePath,
        targetPath: path.join(issue.workspaceDirectory ?? "", ".infcode", "mcpServers", "mcp.json"),
        reason: "workspace-missing",
      })
    }

    for (const target of targets.projects) {
      await migrateProject({ report, projectDir: target.projectDir, projectId: target.projectId })
    }

    return report
  }
}

async function migrateProject(input: { report: McpMigrationReport; projectDir: string; projectId?: string }) {
  const sourcePath = InfcodeMcpConfig.projectPath(input.projectDir)
  if (!(await exists(sourcePath))) return

  let original: string
  try {
    original = await fs.readFile(sourcePath, "utf8")
  } catch (err) {
    addMigrationItem(input.report, {
      status: "failed",
      scope: "project",
      sourcePath,
      reason: "read-failed",
      message: errorMessage(err),
    })
    return
  }
  if (original.trim() === "") return

  const parsed = parseDocument(original)
  if (!parsed.ok) {
    addMigrationItem(input.report, {
      status: "failed",
      scope: "project",
      sourcePath,
      reason: "invalid-json",
      message: parsed.message,
    })
    return
  }

  const conversions = Object.entries(parsed.document.mcpServers).map(([name, value]) => convertServer(name, value))
  const needsProjectId = conversions.some((item) => item.enabled !== undefined)
  if (needsProjectId && !input.projectId) {
    addMigrationItem(input.report, {
      status: "failed",
      scope: "project",
      sourcePath,
      reason: "missing-project-id",
      message: "MCP enabled state cannot be migrated without a project id",
    })
    return
  }

  const nextServers: Record<string, unknown> = {}
  for (const conversion of conversions) {
    nextServers[conversion.name] = conversion.value
    if (conversion.status === "migrated") {
      addMigrationItem(input.report, {
        status: "migrated",
        scope: "project",
        sourcePath,
        targetPath: sourcePath,
        serverName: conversion.name,
        reason: conversion.reason,
      })
    } else if (conversion.status === "failed") {
      addMigrationItem(input.report, {
        status: "failed",
        scope: "project",
        sourcePath,
        targetPath: sourcePath,
        serverName: conversion.name,
        reason: conversion.reason,
        message: conversion.message,
      })
    }
  }

  if (input.projectId) {
    for (const conversion of conversions) {
      if (conversion.enabled === undefined) continue
      await InfcodeMcpEnabled.set(input.projectId, conversion.name, conversion.enabled)
    }
  }

  const nextDocument: McpDocument = { ...parsed.document, mcpServers: nextServers }
  if (!shouldRewrite(parsed.document, nextDocument, conversions)) return

  try {
    const backupPath = await writeUniqueBackup(path.join(path.dirname(sourcePath), "mcp.v1.backup.json"), original)
    await InfcodeMcpConfig.writeDocument(input.projectDir, nextDocument)
    addMigrationItem(input.report, {
      status: "rewritten",
      scope: "project",
      sourcePath,
      targetPath: sourcePath,
      backupPath,
      reason: "canonicalized-project-mcp-json",
    })
  } catch (err) {
    const message = errorMessage(err)
    log.warn("failed to rewrite v1 MCP config", { sourcePath, error: message })
    addMigrationItem(input.report, {
      status: "failed",
      scope: "project",
      sourcePath,
      targetPath: sourcePath,
      reason: "rewrite-failed",
      message,
    })
  }
}

async function discoverMcpProjects(input: {
  home: string
  projectDir?: string
  projectId?: string
}): Promise<{
  projects: Array<{ projectDir: string; projectId?: string }>
  /**
   * Non-fatal problems from project discovery, not MCP server conversion failures.
   *
   * `sessions-json-invalid` means v1 `sessions/sessions.json` could not be parsed, so historical
   * project discovery is unavailable.
   * `workspace-missing` means a workspace path listed in v1 sessions no longer exists, so that
   * project's `.infcode/mcpServers/mcp.json` cannot be inspected.
   *
   * Callers turn these into migration report items, while still migrating every project in `projects`.
   */
  issues: ProjectDiscoveryIssue[]
}> {
  const discovered = await discoverProjectDirectories(input.home)
  const byDir = new Map<string, { projectDir: string; projectId?: string }>()
  for (const projectDir of discovered.projects) {
    byDir.set(projectDir, { projectDir, projectId: await resolveProjectId(projectDir) })
  }
  if (input.projectDir && !byDir.has(input.projectDir)) {
    byDir.set(input.projectDir, {
      projectDir: input.projectDir,
      projectId: input.projectId ?? (await resolveProjectId(input.projectDir)),
    })
  } else if (input.projectDir && input.projectId) {
    byDir.set(input.projectDir, { projectDir: input.projectDir, projectId: input.projectId })
  }
  return { projects: [...byDir.values()], issues: discovered.issues }
}

async function resolveProjectId(projectDir: string): Promise<string | undefined> {
  try {
    return await Instance.provide({ directory: projectDir, fn: () => Instance.project.id })
  } catch (err) {
    log.warn("failed to resolve project id for v1 MCP migration", { projectDir, error: errorMessage(err) })
    return undefined
  }
}

function parseDocument(text: string):
  | {
      ok: true
      document: McpDocument
    }
  | {
      ok: false
      message: string
    } {
  try {
    const parsed = JSON.parse(text) as unknown
    if (!isRecord(parsed)) return { ok: false, message: "MCP config root is not an object" }
    return {
      ok: true,
      document: {
        name: typeof parsed.name === "string" ? parsed.name : "New MCP server",
        version: typeof parsed.version === "string" ? parsed.version : "0.0.1",
        schema: typeof parsed.schema === "string" ? parsed.schema : "v1",
        mcpServers: toServerMap(parsed.mcpServers),
      },
    }
  } catch (err) {
    return { ok: false, message: errorMessage(err) }
  }
}

function toServerMap(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) {
    const out: Record<string, unknown> = {}
    for (const entry of value) {
      if (!isRecord(entry)) continue
      const name = typeof entry.name === "string" ? entry.name.trim() : ""
      if (!name) continue
      const { name: _name, ...rest } = entry
      out[name] = rest
    }
    return out
  }
  return isRecord(value) ? value : {}
}

function convertServer(name: string, value: unknown): ServerConversion {
  if (!isRecord(value)) {
    return {
      name,
      status: "failed",
      value,
      reason: "invalid-server-entry",
      message: "MCP server entry is not an object",
    }
  }

  const normalized = normalizeServerEntry(value)
  const parsed = ConfigMCP.Info.zod.safeParse(normalized)
  if (!parsed.success) {
    return {
      name,
      status: "failed",
      value,
      enabled: enabledState(value),
      reason: "invalid-server-schema",
      message: parsed.error.message,
    }
  }

  const changed = hasLegacyFields(value) || stableStringify(parsed.data) !== stableStringify(value)
  return {
    name,
    status: changed ? "migrated" : "skipped",
    value: parsed.data,
    enabled: enabledState(value),
    reason: changed ? "normalized-server-entry" : undefined,
  }
}

function hasLegacyFields(value: Record<string, unknown>) {
  return (
    typeof value.command === "string" ||
    "args" in value ||
    "env" in value ||
    "connectionTimeout" in value ||
    "disabled" in value ||
    value.type === "sse" ||
    value.type === "streamable-http" ||
    (typeof value.url === "string" && value.type !== "remote")
  )
}

function enabledState(value: Record<string, unknown>): boolean | undefined {
  if (typeof value.enabled === "boolean") return value.enabled
  if (typeof value.disabled === "boolean") return !value.disabled
  return undefined
}

function shouldRewrite(document: McpDocument, next: McpDocument, conversions: ServerConversion[]) {
  return (
    conversions.some((item) => item.status === "migrated" || item.enabled !== undefined) ||
    stableStringify(document.mcpServers) !== stableStringify(next.mcpServers)
  )
}

async function writeUniqueBackup(basePath: string, content: string): Promise<string> {
  if (!(await exists(basePath))) {
    await fs.writeFile(await ensureParent(basePath), content, "utf8")
    return basePath
  }
  const stamp = new Date().toISOString().replace(/[:.]/g, "-")
  const parsed = path.parse(basePath)
  const candidate = path.join(parsed.dir, `${parsed.name}.${stamp}${parsed.ext}`)
  await fs.writeFile(await ensureParent(candidate), content, "utf8")
  return candidate
}

async function ensureParent(file: string) {
  await fs.mkdir(path.dirname(file), { recursive: true })
  return file
}
